/**
 * Single aws-amplify entry point for auth-shell.
 * Import Amplify/Auth/Analytics only from this module so Vite bundles one singleton.
 */
import { Auth } from "aws-amplify";
import { Credentials } from "@aws-amplify/core";
import { InitializeAmplify, SharedConfig } from "@fmc-digital/core-utils";

import type { RuntimeConfig } from "@/config/auth.config";

import { AUTH_APPLICATION } from "./constants";

let amplifyAnalyticsReady = false;

export function isAmplifyAnalyticsReady(): boolean {
  return amplifyAnalyticsReady;
}

/** Reset ready state (tests only). */
export function resetAmplifyAnalyticsReadyForTesting(): void {
  amplifyAnalyticsReady = false;
}

/**
 * Configures Kinesis analytics (via core-utils) and warms Cognito guest credentials.
 */
export async function configureAmplifyAnalytics(
  runtimeConfig: RuntimeConfig,
): Promise<void> {
  const {
    awsProjectRegion,
    awsCognitoIdentityPoolId,
    awsCognitoRegion,
    awsAnalyticsStreamName,
  } = runtimeConfig;

  amplifyAnalyticsReady = false;

  if (
    !awsProjectRegion ||
    !awsCognitoIdentityPoolId ||
    !awsCognitoRegion ||
    !awsAnalyticsStreamName
  ) {
    console.warn(
      "[App] Amplify analytics config missing; skipping Amplify initialization",
    );
    return;
  }

  try {
    console.log("[App] Initializing Amplify analytics");

    SharedConfig.set("amplify", { application: AUTH_APPLICATION });

    InitializeAmplify({
      aws_project_region: awsProjectRegion,
      aws_cognito_identity_pool_id: awsCognitoIdentityPoolId,
      aws_cognito_region: awsCognitoRegion,
      aws_analytics_stream_name: awsAnalyticsStreamName,
    });

    Auth.configure({
      mandatorySignIn: false,
      identityPoolId: awsCognitoIdentityPoolId,
      region: awsCognitoRegion,
    });

    const credentials = await Credentials.get();
    if (!credentials) {
      console.warn(
        "[App] Amplify credentials unavailable; Kinesis events may fail until credentials load",
      );
      return;
    }

    amplifyAnalyticsReady = true;
    console.log("[App] Amplify initialization completed");
  } catch (error) {
    console.error("[App] Failed to initialize Amplify:", error);
    console.error("[App] Amplify initialization error details:", {
      awsProjectRegion,
      awsCognitoIdentityPoolId,
      awsCognitoRegion,
      awsAnalyticsStreamName,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
